import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { Box, FormControl, InputLabel, MenuItem, Select, Switch } from "@mui/material";
import { getProjectManagementData } from '../../redux/actions/project-management';
import { getCircularProgressColor } from "../../common/utils/circular-progress-color";
import { getFullName } from "../../common/utils/datatable";
import CircularLoader from "../Loaders/CircularLoader";
import moment from 'moment';
import './ProjectAllocationsPopup.css';

const ProjectAllocationsPopup = (props) => {
    const { projectManagementData } = useSelector(({ MODULES }) => MODULES);
    const { allUserDetails } = useSelector(({ USER }) => USER);
    const { vTrackLoader } = useSelector(({ APP_STATE }) => APP_STATE);
    const dispatch = useDispatch();
    
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [count, setCount] = useState(0);
    const [searchKey, setSearchKey] = useState('');
    const [billStatus, setBillStatus] = useState('All');
    const [expiredAllocations, setExpiredAllocations] = useState(false);
    const [sortBy, setSortBy] = useState('employeeName');
    const [sortDir, setSortDir] = useState('ASC');

    // Function to get employee name from ID
    const getEmployeeName = (id) => {
        if (!id) return '-';
        let employeeName = "";
        if (allUserDetails && allUserDetails.data && allUserDetails.data.length) {
            allUserDetails.data.forEach((user) => {
                if (user.id && id && user.id.toLowerCase() === id.toLowerCase()) {
                    employeeName = getFullName(user.firstName, user.lastName);
                }
            });
        }
        return employeeName || id; // Return ID as fallback if name not found
    };

    const columns = [
        { id: "employeeName", label: "Employee Name", minWidth: 150, align: 'left' },
        { id: "employeeRole", label: "Project Role", minWidth: 120, align: 'left' },
        { id: "sowName", label: "SOW", minWidth: 140, align: 'left' },
        { id: "site", label: "Location", minWidth: 100, align: 'left' },
        { id: 'startDate', label: 'Start Date', minWidth: 100, align: 'left' },
        { id: "endDate", label: "End Date", minWidth: 100, align: 'left' },
        { id: 'billCurrency', label: 'Bill Currency', minWidth: 80, align: 'left' },
        { id: 'billRate', label: 'Bill Rate', minWidth: 80, align: 'left' },
        { id: 'billFrequency', label: 'Bill Frequency', minWidth: 100, align: 'left' },
        { id: 'billAllocation', label: 'Allocation', minWidth: 80, align: 'left' },
        { id: 'billStatus', label: 'Bill Status', minWidth: 100, align: 'left' },
    ];

    const handleClose = () => {
        props.onResponse(true);
    };

    const fetchAllocations = (pageNo = 1, pageSize = 10) => {
        dispatch(getProjectManagementData({
            projectId: props.row.projectId,
            pageNo: pageNo,
            pageSize: pageSize,
            sortBy: sortBy,
            sortDir: sortDir,
            searchData: searchKey,
            billStatus: billStatus,
            expiredAllocations: expiredAllocations
        }));
    };

    useEffect(() => {
        if (props.row && props.row.projectId) {
            fetchAllocations(1, 100);
        }
    }, [props.row]);

    useEffect(() => {
        if (projectManagementData && projectManagementData.data) {
            // Separate active and expired allocations
            const currentDate = moment().format('YYYY-MM-DD');
            let activeAllocations = [];
            let expiredAllocationsList = [];
            
            projectManagementData.data.forEach((row) => {
                const endDate = row.endDate ? moment(row.endDate).format('YYYY-MM-DD') : null;
                const isExpired = endDate && endDate < currentDate;
                
                if (isExpired) {
                    expiredAllocationsList.push({ ...row, isExpired: true });
                } else {
                    activeAllocations.push({ ...row, isExpired: false });
                }
            });
            
            // Combine active allocations first, then expired
            const sortedRows = [...activeAllocations, ...expiredAllocationsList];
            setRows(sortedRows);
            setCount(projectManagementData.totalCount || projectManagementData.data.length);
        }
    }, [projectManagementData]);

    useEffect(() => {
        if (props.row && props.row.projectId) {
            fetchAllocations(page + 1, rowsPerPage);
        }
    }, [page, rowsPerPage, billStatus, expiredAllocations, sortBy, sortDir]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            setPage(0);
            fetchAllocations(1, rowsPerPage);
        }
    };

    const handleBillStatusChange = (event) => {
        setBillStatus(event.target.value);
        setPage(0);
    };

    const handleExpiredAllocationsChange = (event) => {
        setExpiredAllocations(event.target.checked);
        setPage(0);
    };

    const extractDate = (datetime) => {
        if (!datetime) return '-';
        return datetime.split('T')[0];
    };

    // Format decimal values to 2 decimal places
    const formatDecimal = (value) => {
        if (value === null || value === undefined || value === '') return '-';
        const num = parseFloat(value);
        if (isNaN(num)) return '-';
        // Check if it has decimal places
        if (Number.isInteger(num)) {
            return num.toString();
        }
        return num.toFixed(2);
    };

    const getBillStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'billable':
                return '#4caf50';
            case 'non-billable':
                return '#ff9800';
            case 'shadow':
                return '#9c27b0';
            default:
                return '#757575';
        }
    };

    // Calculate summary statistics
    const getSummaryStats = () => {
        if (!rows || rows.length === 0) return { total: 0, billable: 0, nonBillable: 0, shadow: 0 };
        
        const stats = {
            total: rows.length,
            billable: rows.filter(r => r.billStatus?.toLowerCase() === 'billable').length,
            nonBillable: rows.filter(r => r.billStatus?.toLowerCase() === 'non-billable').length,
            shadow: rows.filter(r => r.billStatus?.toLowerCase() === 'shadow').length,
        };
        return stats;
    };

    const stats = getSummaryStats();

    return (
        <React.Fragment>
            <DialogContent className="project-allocations-dialog">
                {/* Project Header */}
                <div className="project-header">
                    <div className="project-title">
                        <h2>Project Allocations</h2>
                    </div>
                    <div className="project-info-grid">
                        <div className="project-info-item">
                            <label>Project Name</label>
                            <span>{props.row.projectName}</span>
                        </div>
                        <div className="project-info-item">
                            <label>Client Name</label>
                            <span>{props.row.clientName}</span>
                        </div>
                        <div className="project-info-item">
                            <label>Project Manager</label>
                            <span>{props.row.projectManagerName || '-'}</span>
                        </div>
                        <div className="project-info-item">
                            <label>SOW Period</label>
                            <span>{extractDate(props.row.sowStartDate)} to {extractDate(props.row.sowEndDate)}</span>
                        </div>
                    </div>
                </div>

                {/* Summary Statistics */}
                <div className="allocation-summary">
                    <div className="summary-card total">
                        <span className="summary-value">{stats.total}</span>
                        <span className="summary-label">Total Allocations</span>
                    </div>
                    <div className="summary-card billable">
                        <span className="summary-value">{stats.billable}</span>
                        <span className="summary-label">Billable</span>
                    </div>
                    <div className="summary-card non-billable">
                        <span className="summary-value">{stats.nonBillable}</span>
                        <span className="summary-label">Non-Billable</span>
                    </div>
                    <div className="summary-card shadow">
                        <span className="summary-value">{stats.shadow}</span>
                        <span className="summary-label">Shadow</span>
                    </div>
                </div>

                {/* Filters */}
                <div className="filters-container">
                    <TextField
                        className="search-field"
                        placeholder="Search by employee name..."
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.target.value)}
                        onKeyPress={handleSearch}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" className="bill-status-filter">
                        <InputLabel>Bill Status</InputLabel>
                        <Select
                            value={billStatus}
                            label="Bill Status"
                            onChange={handleBillStatusChange}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Billable">Billable</MenuItem>
                            <MenuItem value="Non-Billable">Non-Billable</MenuItem>
                            <MenuItem value="Shadow">Shadow</MenuItem>
                        </Select>
                    </FormControl>
                    <div className="expired-toggle">
                        <label>Show Expired</label>
                        <Switch
                            checked={expiredAllocations}
                            onChange={handleExpiredAllocationsChange}
                            inputProps={{ 'aria-label': 'Show expired allocations' }}
                        />
                    </div>
                </div>

                {/* Loading State */}
                {vTrackLoader && <CircularLoader />}

                {/* Allocations Table */}
                {!vTrackLoader && (
                    <Paper sx={{ width: "100%", overflow: "hidden" }}>
                        <TableContainer sx={{ maxHeight: "40rem" }}>
                            <Table stickyHeader aria-label="project allocations table" size="small">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.align}
                                                style={{ minWidth: column.minWidth }}
                                                sx={{
                                                    backgroundColor: "#1773bc",
                                                    color: "#ffffff",
                                                    fontWeight: 700,
                                                    fontSize: '1.2rem'
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows && rows.length > 0 ? (
                                        rows.map((row, rowIdx) => {
                                            // Check for section headers
                                            const isFirstActive = rowIdx === 0 && !row.isExpired;
                                            const isFirstExpired = rowIdx > 0 && 
                                                !rows[rowIdx - 1].isExpired && 
                                                row.isExpired;

                                            return (
                                                <React.Fragment key={rowIdx}>
                                                    {/* Active Allocations Header */}
                                                    {isFirstActive && (
                                                        <TableRow>
                                                            <TableCell 
                                                                colSpan={columns.length}
                                                                sx={{ 
                                                                    backgroundColor: '#e8f5e9', 
                                                                    fontWeight: 'bold', 
                                                                    fontSize: '1.3rem',
                                                                    color: '#2e7d32',
                                                                    padding: '10px 16px'
                                                                }}
                                                            >
                                                                🟢 ACTIVE ALLOCATIONS
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                    
                                                    {/* Expired Allocations Header */}
                                                    {isFirstExpired && (
                                                        <TableRow>
                                                            <TableCell 
                                                                colSpan={columns.length}
                                                                sx={{ 
                                                                    backgroundColor: '#ffebee', 
                                                                    fontWeight: 'bold', 
                                                                    fontSize: '1.3rem',
                                                                    color: '#d32f2f',
                                                                    padding: '10px 16px'
                                                                }}
                                                            >
                                                                🔴 EXPIRED ALLOCATIONS
                                                            </TableCell>
                                                        </TableRow>
                                                    )}

                                                    <TableRow
                                                        hover
                                                        sx={{
                                                            backgroundColor: row.isExpired ? '#fff5f5' : 'inherit',
                                                            '&:hover': {
                                                                backgroundColor: row.isExpired ? '#ffebee' : '#f5f5f5'
                                                            }
                                                        }}
                                                    >
                                                        {columns.map((col) => (
                                                            <TableCell key={col.id} sx={{ fontSize: '1.2rem' }}>
                                                                {col.id === 'employeeName' ? (
                                                                    // Look up employee name from employeeId
                                                                    row.employeeName || getEmployeeName(row.employeeId) || '-'
                                                                ) : col.id === 'billAllocation' ? (
                                                                    row[col.id] !== null && row[col.id] !== undefined ? (
                                                                        <div className="allocation-cell">
                                                                            <Box sx={{ position: 'relative', display: 'inline-flex', marginRight: '8px' }}>
                                                                                <CircularProgress
                                                                                    variant="determinate"
                                                                                    sx={{
                                                                                        color: (theme) =>
                                                                                            theme.palette.grey[200],
                                                                                    }}
                                                                                    size={24}
                                                                                    thickness={4}
                                                                                    value={100}
                                                                                />
                                                                                <CircularProgress
                                                                                    variant="determinate"
                                                                                    value={parseFloat(row[col.id]) || 0}
                                                                                    thickness={4}
                                                                                    style={getCircularProgressColor(parseFloat(row[col.id]) || 0)}
                                                                                    size={24}
                                                                                    sx={{ position: 'absolute', left: 0 }}
                                                                                />
                                                                            </Box>
                                                                            <span>{formatDecimal(row[col.id])}%</span>
                                                                        </div>
                                                                    ) : '-'
                                                                ) : col.id === 'costAllocation' ? (
                                                                    // Format cost allocation to 2 decimal places
                                                                    row[col.id] !== null && row[col.id] !== undefined ? 
                                                                        `${formatDecimal(row[col.id])}%` : '-'
                                                                ) : col.id === 'billStatus' ? (
                                                                    <span 
                                                                        className="bill-status-badge"
                                                                        style={{ 
                                                                            backgroundColor: getBillStatusColor(row[col.id]),
                                                                            color: '#fff',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '1.1rem'
                                                                        }}
                                                                    >
                                                                        {row[col.id] || '-'}
                                                                    </span>
                                                                ) : col.id === 'startDate' || col.id === 'endDate' ? (
                                                                    extractDate(row[col.id])
                                                                ) : (
                                                                    row[col.id] || '-'
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={columns.length} align="center" sx={{ fontSize: '1.3rem', padding: '2rem' }}>
                                                No allocations found for this project
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={count}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleClose}>Close</Button>
            </DialogActions>
        </React.Fragment>
    );
};

export default ProjectAllocationsPopup;
